import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPostBySlug, posts } from "@/data/blogPosts";

const renderContent = (content: string) => {
  const blocks = content.trim().split(/\n\n+/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={i} className="text-2xl font-bold font-heading text-foreground mt-10 mb-4">
          {trimmed.slice(3)}
        </h2>
      );
    }
    if (trimmed.startsWith("```")) {
      const code = trimmed.replace(/^```\w*\n?/, "").replace(/```$/, "");
      return (
        <pre key={i} className="bg-muted border border-border rounded-lg p-4 overflow-x-auto text-xs my-4">
          <code className="font-mono text-foreground">{code}</code>
        </pre>
      );
    }
    if (/^(-|\d+\.)\s/.test(trimmed)) {
      const isOrdered = /^\d+\.\s/.test(trimmed);
      const items = trimmed.split(/\n/).map((l) => l.replace(/^(-|\d+\.)\s/, ""));
      const ListTag = isOrdered ? "ol" : "ul";
      return (
        <ListTag
          key={i}
          className={`${isOrdered ? "list-decimal" : "list-disc"} pl-6 space-y-2 my-4 text-muted-foreground leading-relaxed`}
        >
          {items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ListTag>
      );
    }
    return (
      <p key={i} className="text-muted-foreground leading-relaxed my-4">
        {renderInline(trimmed)}
      </p>
    );
  });
};

const renderInline = (text: string) => {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code key={i} className="px-1.5 py-0.5 rounded bg-muted font-mono text-xs text-foreground">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) {
      document.title = `${post.title} — Cloudease`;
    }
  }, [post]);

  if (!post) {
    return (
      <Layout>
        <section className="py-32 text-center">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl font-bold font-heading text-foreground mb-4">Article not found</h1>
            <p className="text-muted-foreground mb-6">The post you're looking for doesn't exist.</p>
            <Link to="/blog">
              <Button>Back to blog</Button>
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <Layout>
      <section className="bg-gradient-hero py-24">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-primary-foreground/70 hover:text-primary-foreground text-sm mb-6">
              <ArrowLeft className="h-4 w-4" /> Back to blog
            </Link>
            <span className="inline-block px-3 py-1 text-xs font-semibold bg-primary-foreground/10 text-primary-foreground rounded-full mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold font-heading text-primary-foreground mb-6 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-primary-foreground/70">
              <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> {post.author}</span>
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {post.date}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.readTime}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <article className="py-16 bg-background">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            {renderContent(post.content)}
          </motion.div>

          <div className="mt-16 pt-8 border-t border-border">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 shadow-card">
              <h3 className="font-heading font-semibold text-foreground text-xl mb-2">
                Need help implementing this?
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                Our cloud engineers can bring these practices to your team. Book a free 30-minute consultation.
              </p>
              <Link to="/contact">
                <Button size="lg">Talk to an engineer</Button>
              </Link>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-16 bg-muted/30 border-t border-border">
          <div className="container mx-auto px-4 lg:px-8 max-w-5xl">
            <h2 className="text-2xl font-bold font-heading text-foreground mb-6">Continue reading</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group block bg-card border border-border rounded-xl p-6 shadow-card hover:shadow-elevated transition-all hover:-translate-y-1"
                >
                  <span className="text-xs font-semibold text-primary">{p.category}</span>
                  <h3 className="font-heading font-semibold text-foreground mt-2 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default BlogPost;
